import { ClaimStatus } from '@/api/claims';

interface SummaryCTAsProps {
  isWinner: boolean;
  claimStatus?: ClaimStatus;
  payoutAmount?: number;
  onClaim?: () => void;
  onStartNewChallenge: () => void;
  onShare: () => void;
  isClaiming?: boolean;
  isSharing?: boolean;
  /** Whether share image is pre-generated and ready */
  isShareReady?: boolean;
}

export function SummaryCTAs({
  isWinner,
  claimStatus,
  payoutAmount = 0,
  onClaim,
  onStartNewChallenge,
  onShare,
  isClaiming = false,
  isSharing = false,
  isShareReady = false,
}: SummaryCTAsProps) {
  // Render primary button based on winner status and claim state
  const renderPrimaryButton = () => {
    // Non-winners see "Start New Challenge"
    if (!isWinner) {
      return (
        <button
          onClick={onStartNewChallenge}
          className="w-full h-14 rounded-full bg-primary text-[#0c1c0c] font-bold text-lg flex items-center justify-center gap-2 shadow-[0_0_20px_-5px_rgba(37,244,120,0.5)] active:scale-[0.98] transition-transform"
        >
          Start New Challenge
          <span className="material-symbols-outlined">arrow_forward</span>
        </button>
      );
    }

    // Winner - already claimed
    if (claimStatus === 'CLAIMED') {
      return (
        <button
          disabled
          className="w-full h-14 rounded-full bg-white/10 text-white/60 font-bold text-lg flex items-center justify-center gap-2 cursor-not-allowed"
        >
          <span className="material-symbols-outlined">check_circle</span>
          Claimed ${payoutAmount.toFixed(2)}
        </button>
      );
    }

    // Winner - waiting for merkle root to be set on chain
    if (claimStatus === 'PENDING_ROOT') {
      return (
        <button
          disabled
          className="w-full h-14 rounded-full bg-white/10 text-white/60 font-bold text-lg flex items-center justify-center gap-2 cursor-not-allowed"
        >
          <span className="material-symbols-outlined animate-pulse">hourglass_top</span>
          Claim Available Soon
        </button>
      );
    }

    // Winner - ready to claim
    if (claimStatus === 'CLAIMABLE') {
      return (
        <button
          onClick={onClaim}
          disabled={isClaiming}
          className={`w-full h-14 rounded-full font-bold text-lg flex items-center justify-center gap-2 transition-all ${
            isClaiming
              ? 'bg-primary/70 text-[#0c1c0c]/70 cursor-not-allowed'
              : 'bg-primary text-[#0c1c0c] shadow-[0_0_20px_-5px_rgba(37,244,120,0.5)] active:scale-[0.98]'
          }`}
        >
          {isClaiming ? (
            <>
              <span className="material-symbols-outlined animate-spin">sync</span>
              Claiming...
            </>
          ) : (
            <>
              <span className="material-symbols-outlined">account_balance_wallet</span>
              Claim ${payoutAmount.toFixed(2)}
            </>
          )}
        </button>
      );
    }

    // Fallback for winners without claim status (shouldn't happen)
    return (
      <button
        onClick={onStartNewChallenge}
        className="w-full h-14 rounded-full bg-primary text-[#0c1c0c] font-bold text-lg flex items-center justify-center gap-2 shadow-[0_0_20px_-5px_rgba(37,244,120,0.5)] active:scale-[0.98] transition-transform"
      >
        Start New Challenge
        <span className="material-symbols-outlined">arrow_forward</span>
      </button>
    );
  };

  return (
    <div className="fixed left-0 right-0 p-4 z-50 bottom-[calc(4rem+env(safe-area-inset-bottom))]">
      <div className="flex flex-row gap-3">
        {/* Primary button takes more space */}
        <div className="flex-1">
          {renderPrimaryButton()}
        </div>
        {/* Share button - square icon button */}
        <button
          onClick={onShare}
          disabled={!isShareReady || isSharing}
          className={`h-14 w-14 rounded-full border font-semibold flex items-center justify-center transition-colors shrink-0 ${
            !isShareReady || isSharing
              ? 'bg-primary/30 border-primary/50 text-white/50 cursor-not-allowed'
              : 'bg-primary/20 border-primary/40 text-white active:bg-primary/30'
          }`}
        >
          {isSharing ? (
            <span className="material-symbols-outlined animate-spin text-xl">sync</span>
          ) : !isShareReady ? (
            <span className="material-symbols-outlined animate-pulse text-xl">hourglass_top</span>
          ) : (
            <span className="material-symbols-outlined text-xl">ios_share</span>
          )}
        </button>
      </div>
    </div>
  );
}
